import { useCallback } from "react"
import {
  SlideElement,
  TableElementSettings,
  DataElementConfig,
  ELEMENT_TYPE,
} from "types/ReportingDashboard/elements.types"

/**
 * Coordinate interface
 */
export interface Coordinates {
  x: number
  y: number
}

/**
 * Element size dimensions
 */
export interface ElementSize {
  width: number
  height: number
}

/**
 * Default element sizes mapping
 */
export interface DefaultElementSizes {
  [ELEMENT_TYPE.TEXT]: ElementSize
  [ELEMENT_TYPE.HEADING]: ElementSize
  [ELEMENT_TYPE.TABLE]: ElementSize
  [ELEMENT_TYPE.CHART]: ElementSize
  [ELEMENT_TYPE.PIE_CHART]: ElementSize
  [ELEMENT_TYPE.TIME_SERIES_CHART]: ElementSize
  [ELEMENT_TYPE.IMAGE]: ElementSize
}

/**
 * Return type for useElementConstraints hook
 */
export interface ElementConstraintsResult {
  constrainToBoundaries: (
    id: string | number,
    x: number,
    y: number
  ) => Coordinates
  snapToGrid: (x: number, y: number) => Coordinates
  DEFAULT_ELEMENT_SIZES: DefaultElementSizes
}

/**
 * Custom hook for managing element positioning constraints and grid snapping.
 *
 * Provides functions to constrain element positions within canvas boundaries
 * and snap coordinates to a grid system. Handles special cases for different
 * element types (especially tables with configuration panels).
 *
 * @param elements - Array of canvas elements
 * @param tableConfigs - Configuration data for table elements, keyed by element ID
 * @param GRID_SIZE_UNITS - Size of grid squares in abstract units
 * @param POWERPOINT_WIDTH_UNITS - Canvas width in abstract units
 * @param POWERPOINT_HEIGHT_UNITS - Canvas height in abstract units
 *
 * @returns Constraint and utility functions
 *
 * @example
 * const { constrainToBoundaries, snapToGrid } = useElementConstraints(
 *   elements, tableConfigs, 30, 1440, 810
 * )
 *
 * // Constrain and snap a position
 * const { x: snappedX, y: snappedY } = snapToGrid(rawX, rawY)
 * const { x: finalX, y: finalY } = constrainToBoundaries(elementId, snappedX, snappedY)
 */
export const useElementConstraints = (
  elements: SlideElement[],
  tableConfigs: Record<string, DataElementConfig>,
  GRID_SIZE_UNITS: number,
  POWERPOINT_WIDTH_UNITS: number,
  POWERPOINT_HEIGHT_UNITS: number
): ElementConstraintsResult => {
  const DEFAULT_ELEMENT_SIZES: DefaultElementSizes = {
    [ELEMENT_TYPE.TEXT]: { width: 200, height: 40 },
    [ELEMENT_TYPE.HEADING]: { width: 400, height: 60 },
    [ELEMENT_TYPE.TABLE]: { width: 800, height: 600 },
    [ELEMENT_TYPE.CHART]: { width: 600, height: 400 },
    [ELEMENT_TYPE.PIE_CHART]: { width: 500, height: 500 },
    [ELEMENT_TYPE.TIME_SERIES_CHART]: { width: 700, height: 450 },
    [ELEMENT_TYPE.IMAGE]: { width: 300, height: 200 },
  }

  /**
   * Type guard to check if config is TableElementSettings
   */
  const isTableConfig = (
    config: DataElementConfig
  ): config is TableElementSettings => {
    return "crossTabVariables" in config
  }

  /**
   * Constrains an element's position to prevent it from going completely off-canvas.
   *
   * Allows elements (especially tables) to be partially visible or positioned
   * above the top edge. Special handling for tables with configuration panels.
   *
   * @param id - The ID of the element to constrain
   * @param x - Proposed X coordinate in abstract units
   * @param y - Proposed Y coordinate in abstract units
   * @returns Constrained coordinates
   */
  const constrainToBoundaries = useCallback(
    (id: string | number, x: number, y: number): Coordinates => {
      const element = elements.find((el) => String(el.id) === String(id))
      const defaultSize = element?.type
        ? DEFAULT_ELEMENT_SIZES[element.type]
        : { width: 300, height: 200 }
      const elementWidth = element?.width ?? defaultSize.width
      //const elementHeight = element?.height ?? defaultSize.height
      const minVisibleMargin = 50

      let constrainedX = x
      let constrainedY = y

      if (element?.type === ELEMENT_TYPE.TABLE) {
        const tableConfig = tableConfigs[element.id]
        const hasTableData =
          tableConfig &&
          isTableConfig(tableConfig) &&
          ((tableConfig.crossTabVariables?.length ?? 0) > 0 ||
            (tableConfig.selectedOperators?.length ?? 0) > 0 ||
            Object.keys(tableConfig).length > 0)

        if (!hasTableData) {
          const configPanelWidth = 300
          const configPanelHeight = 150
          constrainedX = Math.max(x, -configPanelWidth + minVisibleMargin)
          constrainedY = Math.max(y, -configPanelHeight + minVisibleMargin)
        } else {
          constrainedX = Math.max(x, -elementWidth + minVisibleMargin)
          constrainedY = Math.max(y, -2500) // Fixed large number to allow lots of upward dragging
        }
      }

      return { x: constrainedX, y: constrainedY }
    },
    [elements, tableConfigs, DEFAULT_ELEMENT_SIZES]
  )

  /**
   * Snaps coordinates to the nearest grid point defined by GRID_SIZE_UNITS.
   *
   * Disables snapping near canvas edges to allow fine-tuning of element positions.
   *
   * @param x - X coordinate in abstract units
   * @param y - Y coordinate in abstract units
   * @returns Snapped coordinates
   */
  const snapToGrid = useCallback(
    (x: number, y: number): Coordinates => {
      const edgeThreshold = GRID_SIZE_UNITS

      if (
        x < edgeThreshold ||
        y < edgeThreshold ||
        x > POWERPOINT_WIDTH_UNITS - edgeThreshold ||
        y > POWERPOINT_HEIGHT_UNITS - edgeThreshold
      ) {
        return { x, y }
      }

      const snappedX = Math.round(x / GRID_SIZE_UNITS) * GRID_SIZE_UNITS
      const snappedY = Math.round(y / GRID_SIZE_UNITS) * GRID_SIZE_UNITS
      return { x: snappedX, y: snappedY }
    },
    [GRID_SIZE_UNITS, POWERPOINT_WIDTH_UNITS, POWERPOINT_HEIGHT_UNITS]
  )

  return { constrainToBoundaries, snapToGrid, DEFAULT_ELEMENT_SIZES }
}
